import { jwtDecode } from 'jwt-decode';

export type AuthToken = string;
let token: AuthToken | null = null;

export function setToken(newToken: AuthToken): void {
    token = newToken;
}

/**
 * Gets the current token. If no token is present, an error is thrown.
 *
 * Read it where the request goes out (a `mutationFn`, a callback), not while rendering: the token is renewed
 * while the pages stay mounted, so one read while rendering is sent after it has expired. A component that has
 * to render with it reads `TokenContext`, which renders it again on renewal.
 */
export function getToken(): AuthToken {
    if (!token) {
        throw new Error(`Token not present, make sure you've set the token before with setToken().`);
    }
    return token;
}

/**
 * Gets the current token if present. If no token is present, undefined is returned. Read it where the request goes
 * out, as `getToken`.
 */
export function getTokenIfPresent(): AuthToken | undefined {
    return token ?? undefined;
}

/** By `sub`, as the tenant fixes the organisation; tokens that can't be decoded count as the same. */
export function isSameUser(first: AuthToken, second: AuthToken): boolean {
    try {
        return jwtDecode(first).sub === jwtDecode(second).sub;
    } catch {
        return true;
    }
}

export function isExpired(token: AuthToken): boolean {
    const decodedToken = jwtDecode(token);
    const expirationDate = decodedToken.exp;
    if (!expirationDate) {
        return false;
    }
    // Info: This might fail if the user's system time is incorrect.
    return expirationDate * 1000 < Date.now();
}
